/* eslint-disable react/jsx-no-undef */
/* eslint-disable react/jsx-no-useless-fragment */
import { lazy, Suspense, useEffect } from "react";
import {
  createBrowserRouter,
  createRoutesFromElements,
  Navigate,
  Outlet,
  Route,
  useLocation,
} from "react-router-dom";

import { logPageView } from "../libs/analytics";
// 直接导入loader函数，它们不需要懒加载
import { builderLoader } from "../pages/builder/page";
import { publicLoader } from "../pages/public/page";
/* Temporarily disabled About page */
/* import AboutMePage from "../pages/aboutme/aboutme"; */
import { Providers } from "../providers";
import { AuthGuard } from "./guards/auth";
import { GuestGuard } from "./guards/guest";
import { authLoader } from "./loaders/auth";

// Lazy-loaded components
const AgentPage = lazy(() =>
  import("../pages/agent/agent").then((module) => ({ default: module.AgentPage })),
);
const ToolTestPage = lazy(() => import("../pages/agent/testtool"));
const ArticleDetailPage = lazy(() => import("../pages/articles/detail"));
const ArticlesPage = lazy(() => import("../pages/articles/page"));
const BackupOtpPage = lazy(() =>
  import("../pages/auth/backup-otp/page").then((module) => ({ default: module.BackupOtpPage })),
);
const ForgotPasswordPage = lazy(() =>
  import("../pages/auth/forgot-password/page").then((module) => ({
    default: module.ForgotPasswordPage,
  })),
);
const AuthLayout = lazy(() =>
  import("../pages/auth/layout").then((module) => ({ default: module.AuthLayout })),
);
const LoginPage = lazy(() =>
  import("../pages/auth/login/page").then((module) => ({ default: module.LoginPage })),
);
const RegisterPage = lazy(() =>
  import("../pages/auth/register/page").then((module) => ({ default: module.RegisterPage })),
);
const ResetPasswordPage = lazy(() =>
  import("../pages/auth/reset-password/page").then((module) => ({
    default: module.ResetPasswordPage,
  })),
);
const VerifyEmailPage = lazy(() =>
  import("../pages/auth/verify-email/page").then((module) => ({ default: module.VerifyEmailPage })),
);
const VerifyOtpPage = lazy(() =>
  import("../pages/auth/verify-otp/page").then((module) => ({ default: module.VerifyOtpPage })),
);
const BuilderLayout = lazy(() =>
  import("../pages/builder/layout").then((module) => ({ default: module.BuilderLayout })),
);
// 单独导入组件
const BuilderPage = lazy(() =>
  import("../pages/builder/page").then((module) => ({ default: module.BuilderPage })),
);
const DashboardLayout = lazy(() =>
  import("../pages/dashboard/layout").then((module) => ({ default: module.DashboardLayout })),
);
const ResumesPage = lazy(() =>
  import("../pages/dashboard/resumes/page").then((module) => ({ default: module.ResumesPage })),
);
const SettingsPage = lazy(() =>
  import("../pages/dashboard/settings/page").then((module) => ({ default: module.SettingsPage })),
);
const HomeLayout = lazy(() =>
  import("../pages/home/layout").then((module) => ({ default: module.HomeLayout })),
);
const PrivacyPolicyPage = lazy(() =>
  import("../pages/home/meta/privacy-policy/page").then((module) => ({
    default: module.PrivacyPolicyPage,
  })),
);
const HomePage = lazy(() =>
  import("../pages/home/page").then((module) => ({ default: module.HomePage })),
);
const PricingPage = lazy(() =>
  import("../pages/pricing/page").then((module) => ({ default: module.PricingPage })),
);
// 单独导入组件
const PublicResumePage = lazy(() =>
  import("../pages/public/page").then((module) => ({ default: module.PublicResumePage })),
);
const WechatLoginPage = lazy(() =>
  import("../pages/auth/wechat/page").then((module) => ({ default: module.WechatLoginPage })),
);
// 个人网站页面
const PersonalWebsitePage = lazy(() =>
  import("../pages/personalwebsite").then((module) => ({ default: module.PersonalWebsitePage })),
);
// 个人网站公开页面
const PublicPersonalWebsitePage = lazy(() =>
  import("../pages/personalwebsite").then((module) => ({
    default: module.PublicPersonalWebsitePage,
  })),
);

// 创建加载中的组件
const LoadingComponent = () => (
  <div className="flex h-screen w-full items-center justify-center">
    <div className="text-center">
      <div className="mx-auto size-10 animate-spin rounded-full border-y-2 border-primary"></div>
      <p className="mt-3">加载中...</p>
    </div>
  </div>
);

// 创建新的 RouterWrapper 组件来处理页面访问记录
const RouterWrapper = ({ children }: { children: React.ReactNode }) => {
  const location = useLocation();

  useEffect(() => {
    logPageView(location.pathname);
  }, [location]);

  return <>{children}</>;
};

export const routes = createRoutesFromElements(
  <Route element={<Providers />}>
    <Route
      element={
        <RouterWrapper>
          <Suspense fallback={<LoadingComponent />}>
            <Outlet />
          </Suspense>
        </RouterWrapper>
      }
    >
      <Route element={<HomeLayout />}>
        <Route path="/" element={<HomePage />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/articles" element={<ArticlesPage />} />
        <Route path="/articles/:slug" element={<ArticleDetailPage />} />

        <Route path="meta">
          <Route path="privacy-policy" element={<PrivacyPolicyPage />} />
          <Route index element={<Navigate replace to="/" />} />
        </Route>
      </Route>

      {/* Temporarily disabled About page */}
      {/* <Route path="/about29" element={<AboutMePage />} /> */}

      <Route path="auth">
        <Route element={<AuthLayout />}>
          <Route element={<GuestGuard />}>
            <Route path="login" element={<LoginPage />} />
            <Route path="register" element={<RegisterPage />} />
          </Route>

          {/* Password Recovery */}
          <Route element={<GuestGuard />}>
            <Route path="forgot-password" element={<ForgotPasswordPage />} />
            <Route path="reset-password" element={<ResetPasswordPage />} />
          </Route>

          {/* Two-Factor Authentication */}
          <Route element={<GuestGuard />}>
            <Route path="verify-otp" element={<VerifyOtpPage />} />
            <Route path="backup-otp" element={<BackupOtpPage />} />
          </Route>

          {/* Email Verification */}
          <Route element={<AuthGuard />}>
            <Route path="verify-email" element={<VerifyEmailPage />} />
          </Route>

          {/* OAuth Callback */}
          <Route path="callback" loader={authLoader} />

          {/* Wechat Login */}
          <Route path="wechat" element={<WechatLoginPage />} />
        </Route>

        <Route index element={<Navigate replace to="/auth/wechat" />} />
      </Route>

      <Route path="dashboard">
        <Route element={<AuthGuard />}>
          <Route element={<DashboardLayout />}>
            <Route path="resumes" element={<ResumesPage />} />
            <Route path="settings" element={<SettingsPage />} />
            <Route path="agent" element={<AgentPage />} />
            <Route path="testtool" element={<ToolTestPage />} />
            <Route path="pricing" element={<PricingPage />} />

            <Route index element={<Navigate replace to="/dashboard/resumes" />} />
          </Route>
        </Route>
      </Route>

      {/* 个人网站管理页面（独立路由，需要登录） */}
      <Route path="personalwebsite">
        <Route element={<AuthGuard />}>
          <Route path=":id?" element={<PersonalWebsitePage />} />
          <Route index element={<PersonalWebsitePage />} />
        </Route>
      </Route>

      <Route path="builder">
        <Route element={<AuthGuard />}>
          <Route element={<BuilderLayout />}>
            <Route path=":id" loader={builderLoader} element={<BuilderPage />} />

            <Route index element={<Navigate replace to="/dashboard/resumes" />} />
          </Route>
        </Route>
      </Route>

      {/* Public Routes */}
      <Route path=":username">
        <Route path=":id" loader={publicLoader} element={<PublicResumePage />} />
      </Route>

      {/* 个人网站公开访问路由 */}
      <Route path="p">
        <Route path=":userId/:websiteId" element={<PublicPersonalWebsitePage />} />
      </Route>

      {/* 个人网站自定义URL访问路由 */}
      <Route path="profile">
        <Route path=":customUrl" element={<PublicPersonalWebsitePage />} />
      </Route>
    </Route>
  </Route>,
);

export const router = createBrowserRouter(routes);
